'use client';

import {
  IconAlertCircle,
  IconBell,
  IconCheck,
  IconCopy,
  IconDeviceDesktop,
  IconExternalLink,
  IconFileText,
  IconInbox,
  IconLoader2,
  IconMail,
  IconMessage,
  IconPhone,
  IconPhoneCall,
  IconRefresh,
  IconSend,
  IconShieldCheck,
  IconUser,
} from '@tabler/icons-react';
import { useEffect, useState } from 'react';

import { Link } from '@j-s/components';
import { isProduction } from '@j-s/helpers';

import { ClientPushPromptCard } from '../components/ClientPushPromptCard';
import { MyTicketsDrawer } from '../components/MyTicketsDrawer';
import { TicketTrackerModal } from '../components/TicketTrackerModal';
import { useClientTickets } from '../hooks/useClientTickets';
import { openCookieSettings } from '../hooks/useCookieConsent';

import { useContactForm } from './useContactForm';

export interface FooterProps {
  title?: string;
  subtitle?: string;
  copyrightText?: string;
  termsHref?: string;
}

export function Footer({
  title = 'Masz pytanie? Napisz',
  subtitle = 'Chętnie odpowiemy na Twoje pytania dotyczące serwisu, części rowerowych i wycieczek.',
  copyrightText = '© 2025 Dom Rowerowy. All Rights Reserved',
  termsHref = '/terms',
}: FooterProps) {
  const {
    name,
    setName,
    contactMethod,
    setContactMethod,
    phoneResponsePreference,
    setPhoneResponsePreference,
    email,
    setEmail,
    phone,
    setPhone,
    subject,
    setSubject,
    message,
    setMessage,
    terms,
    setTerms,
    appCheckStatus,
    verifyAppCheck,
    triggerVerificationIfNeeded,
    isSubmitting,
    isSuccess,
    createdTicket,
    statusMsg,
    errorMsg,
    canSubmit,
    handleSubmit,
    resetForm,
  } = useContactForm();

  const {
    savedTickets,
    ticketCount,
    activeTrackingId,
    activeTrackingToken,
    isDrawerOpen,
    setIsDrawerOpen,
    isModalOpen,
    openTicket,
    closeTicketModal,
    removeTicket,
  } = useClientTickets();

  const [copiedLink, setCopiedLink] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const params = new URLSearchParams(window.location.search);
    const hash = window.location.hash;
    const ticketId =
      params.get('ticket') ||
      (hash.includes('ticket=')
        ? new URLSearchParams(hash.split('?')[1]).get('ticket')
        : null);
    const token =
      params.get('token') ||
      (hash.includes('token=')
        ? new URLSearchParams(hash.split('?')[1]).get('token')
        : null);

    if (ticketId && token) {
      openTicket(ticketId, token);
    }
  }, [openTicket]);

  const handleCopyMagicLink = () => {
    if (!createdTicket || typeof window === 'undefined') return;

    const url = `${window.location.origin}/#napisz-do-nas?ticket=${createdTicket.id}&token=${createdTicket.accessToken}`;

    navigator.clipboard.writeText(url);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2500);
  };

  const handleOpenCreatedTicket = () => {
    if (!createdTicket) return;

    openTicket(createdTicket.id, createdTicket.accessToken);
  };

  return (
    <footer
      id="napisz-do-nas"
      className="w-full bg-[#18181b] text-white rounded-t-3xl pt-16 pb-10 px-4 sm:px-6 lg:px-8 mt-12 transition-colors"
      style={{ backgroundColor: '#18181b', color: '#ffffff' }}
    >
      <div className="max-w-4xl mx-auto flex flex-col items-center gap-8">
        {/* Header section */}
        <div className="flex flex-col items-center text-center max-w-xl">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-[#eded1e]/10 text-[#eded1e] border border-[#eded1e]/20 mb-3 tracking-wide uppercase">
            Formularz kontaktowy
          </span>
          <h2
            className="!text-white font-permanent-marker text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight m-0"
            style={{
              color: '#ffffff',
              fontFamily: 'var(--font-permanent-marker), cursive',
            }}
          >
            {title}
          </h2>
          {subtitle && (
            <p className="text-zinc-400 text-sm sm:text-base mt-3 leading-relaxed">
              {subtitle}
            </p>
          )}
        </div>

        {/* Previous threads banner */}
        {ticketCount > 0 && (
          <div className="w-full max-w-2xl bg-zinc-900/95 border border-zinc-700/80 rounded-2xl p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-xl backdrop-blur-md">
            <div className="flex items-center gap-3.5">
              <div className="w-10 h-10 rounded-xl bg-[#eded1e]/15 border border-[#eded1e]/30 flex items-center justify-center text-[#eded1e] shrink-0">
                <IconInbox size={22} />
              </div>
              <div className="space-y-0.5">
                <div className="text-sm font-bold text-white flex items-center gap-2">
                  <span>Twoje wiadomości ({ticketCount})</span>
                  {savedTickets.some((t) => t.lastStatus === 'replied') && (
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500 text-white animate-pulse">
                      Nowa odpowiedź!
                    </span>
                  )}
                </div>
                <div className="text-xs text-zinc-400">
                  Historia Twoich zapytań i odpowiedzi serwisu w tej
                  przeglądarce
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setIsDrawerOpen(true)}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-[#eded1e] text-zinc-950 font-bold text-xs hover:bg-[#f6f62a] transition-all cursor-pointer shadow-sm shrink-0 self-stretch sm:self-auto justify-center"
            >
              <span>Zobacz wiadomości</span>
              <IconExternalLink size={15} />
            </button>
          </div>
        )}

        {/* Contact Form Card */}
        <div className="w-full max-w-2xl bg-zinc-900/90 border border-zinc-800 rounded-2xl p-6 sm:p-8 md:p-10 shadow-2xl shadow-black/50 backdrop-blur-md">
          {isSuccess ? (
            <div className="flex flex-col items-center text-center py-6 gap-5">
              <div className="w-16 h-16 rounded-full bg-[#eded1e]/10 border-2 border-[#eded1e] flex items-center justify-center text-[#eded1e] shadow-lg shadow-[#eded1e]/20 animate-bounce">
                <IconCheck size={36} stroke={2.5} />
              </div>
              <div className="space-y-2">
                <h3
                  className="!text-white text-xl sm:text-2xl font-bold m-0"
                  style={{ color: '#ffffff' }}
                >
                  Wiadomość wysłana!
                </h3>
                <p
                  className="!text-zinc-300 text-sm sm:text-base max-w-md m-0"
                  style={{ color: '#d4d4d8' }}
                >
                  {statusMsg ||
                    'Dziękujemy za kontakt! Odpowiemy najszybciej jak to możliwe.'}
                </p>
              </div>

              {createdTicket &&
                (contactMethod === 'phone' &&
                phoneResponsePreference === 'call' ? (
                  <div className="w-full bg-zinc-950/60 border border-zinc-700/80 rounded-xl p-4 flex flex-col items-start gap-2 text-left">
                    <span className="text-[11px] text-zinc-400 block uppercase tracking-wider font-semibold">
                      Numer Twojego zgłoszenia:
                    </span>
                    <span className="text-xl font-mono font-bold text-[#eded1e]">
                      {createdTicket.ticketNumber}
                    </span>
                    <p className="text-xs text-zinc-300 m-0">
                      Zgłoszenie telefoniczne zostało zarejestrowane. Pracownik
                      serwisu skontaktuje się z Tobą pod numerem:{' '}
                      <strong className="text-white">{phone}</strong>.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="w-full bg-zinc-950/60 border border-zinc-700/80 rounded-xl p-4 flex flex-col sm:flex-row items-center justify-between gap-3 text-left">
                      <div>
                        <span className="text-[11px] text-zinc-400 block uppercase tracking-wider font-semibold">
                          Numer Twojej wiadomości:
                        </span>
                        <span className="text-lg font-mono font-bold text-[#eded1e]">
                          {createdTicket.ticketNumber}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={handleOpenCreatedTicket}
                          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-[#eded1e] text-zinc-950 font-bold text-xs hover:bg-[#f6f62a] transition-all cursor-pointer shadow-xs"
                        >
                          <IconExternalLink size={14} />
                          <span>Pokaż wątek</span>
                        </button>
                        <button
                          type="button"
                          onClick={handleCopyMagicLink}
                          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-xs font-medium border border-zinc-700 transition-all cursor-pointer"
                        >
                          {copiedLink ? (
                            <IconCheck size={14} />
                          ) : (
                            <IconCopy size={14} />
                          )}
                          <span>
                            {copiedLink ? 'Skopiowano!' : 'Kopiuj link'}
                          </span>
                        </button>
                      </div>
                    </div>

                    <ClientPushPromptCard
                      ticketId={createdTicket.id}
                      ticketNumber={createdTicket.ticketNumber}
                    />
                  </>
                ))}

              <button
                type="button"
                onClick={resetForm}
                className="mt-2 inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-white font-medium text-sm transition-colors border border-zinc-700 cursor-pointer"
              >
                <IconRefresh size={16} />
                Napisz kolejną wiadomość
              </button>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              onFocusCapture={triggerVerificationIfNeeded}
              onPointerDownCapture={triggerVerificationIfNeeded}
              className="flex flex-col gap-5 w-full"
              aria-label="Formularz kontaktowy"
              noValidate
            >
              {/* Row 1: Name & Contact (Email or Phone) */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <label
                    htmlFor="contact-name"
                    className="text-xs sm:text-sm font-medium text-zinc-300 flex items-center gap-1.5 h-6"
                  >
                    <IconUser
                      size={15}
                      className="text-zinc-400"
                      aria-hidden="true"
                    />
                    Twoje Imię <span className="text-rose-400">*</span>
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    aria-label="Twoje Imię"
                    autoComplete="name"
                    placeholder="np. Jan Kowalski"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full bg-zinc-800/90 hover:bg-zinc-800 focus:bg-zinc-850 text-white placeholder:text-zinc-500 border border-zinc-700/80 focus:border-[#eded1e] focus:ring-2 focus:ring-[#eded1e]/20 rounded-xl px-4 py-3 text-base outline-none transition-all"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center justify-between h-6">
                    <label
                      htmlFor={
                        contactMethod === 'email'
                          ? 'contact-email'
                          : 'contact-phone'
                      }
                      className="text-xs sm:text-sm font-medium text-zinc-300 flex items-center gap-1.5"
                    >
                      {contactMethod === 'email' ? (
                        <>
                          <IconMail
                            size={15}
                            className="text-zinc-400"
                            aria-hidden="true"
                          />
                          <span>Adres E-mail</span>
                        </>
                      ) : (
                        <>
                          <IconPhone
                            size={15}
                            className="text-zinc-400"
                            aria-hidden="true"
                          />
                          <span>Nr telefonu</span>
                        </>
                      )}
                      <span className="text-rose-400">*</span>
                    </label>

                    {/* Segmented Switch */}
                    <div
                      role="radiogroup"
                      aria-label="Wybierz preferowaną formę kontaktu"
                      className="inline-flex p-0.5 rounded-lg bg-zinc-800/90 border border-zinc-700/80"
                    >
                      <button
                        type="button"
                        role="radio"
                        aria-checked={contactMethod === 'email'}
                        onClick={() => setContactMethod('email')}
                        className={`flex items-center gap-1 px-2.5 py-0.5 rounded-md text-[11px] font-medium transition-all cursor-pointer ${
                          contactMethod === 'email'
                            ? 'bg-[#eded1e] text-zinc-950 font-bold shadow-xs'
                            : 'text-zinc-400 hover:text-zinc-200'
                        }`}
                      >
                        <IconMail size={12} aria-hidden="true" />
                        <span>E-mail</span>
                      </button>
                      <button
                        type="button"
                        role="radio"
                        aria-checked={contactMethod === 'phone'}
                        onClick={() => setContactMethod('phone')}
                        className={`flex items-center gap-1 px-2.5 py-0.5 rounded-md text-[11px] font-medium transition-all cursor-pointer ${
                          contactMethod === 'phone'
                            ? 'bg-[#eded1e] text-zinc-950 font-bold shadow-xs'
                            : 'text-zinc-400 hover:text-zinc-200'
                        }`}
                      >
                        <IconPhone size={12} aria-hidden="true" />
                        <span>Telefon</span>
                      </button>
                    </div>
                  </div>

                  {contactMethod === 'email' ? (
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      aria-label="Adres E-mail"
                      autoComplete="email"
                      placeholder="np. jan@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full bg-zinc-800/90 hover:bg-zinc-800 focus:bg-zinc-850 text-white placeholder:text-zinc-500 border border-zinc-700/80 focus:border-[#eded1e] focus:ring-2 focus:ring-[#eded1e]/20 rounded-xl px-4 py-3 text-base outline-none transition-all"
                    />
                  ) : (
                    <div className="space-y-3">
                      <input
                        id="contact-phone"
                        name="phone"
                        type="tel"
                        required
                        aria-label="Numer telefonu / Kod kierunkowy"
                        autoComplete="tel"
                        placeholder="np. 606 782 670"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        className="w-full bg-zinc-800/90 hover:bg-zinc-800 focus:bg-zinc-850 text-white placeholder:text-zinc-500 border border-zinc-700/80 focus:border-[#eded1e] focus:ring-2 focus:ring-[#eded1e]/20 rounded-xl px-4 py-3 text-base outline-none transition-all"
                      />

                      <div className="flex flex-col gap-1.5 pt-0.5">
                        <span className="text-xs font-medium text-zinc-300">
                          Preferowany sposób otrzymania odpowiedzi:
                        </span>
                        <div
                          role="radiogroup"
                          aria-label="Wybierz preferowany sposób otrzymania odpowiedzi"
                          className="grid grid-cols-1 sm:grid-cols-2 gap-2"
                        >
                          <button
                            type="button"
                            role="radio"
                            aria-checked={phoneResponsePreference === 'call'}
                            onClick={() => setPhoneResponsePreference('call')}
                            className={`flex items-center gap-2.5 p-3 rounded-xl border text-xs font-medium transition-all text-left cursor-pointer ${
                              phoneResponsePreference === 'call'
                                ? 'bg-[#eded1e]/15 border-[#eded1e] text-white shadow-xs'
                                : 'bg-zinc-800/60 border-zinc-700/80 text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800'
                            }`}
                          >
                            <div
                              className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${
                                phoneResponsePreference === 'call'
                                  ? 'border-[#eded1e] bg-[#eded1e]'
                                  : 'border-zinc-500'
                              }`}
                            >
                              {phoneResponsePreference === 'call' && (
                                <div className="w-1.5 h-1.5 rounded-full bg-zinc-950" />
                              )}
                            </div>
                            <div className="flex flex-col min-w-0">
                              <span className="font-semibold text-white flex items-center gap-1.5">
                                <IconPhoneCall
                                  size={14}
                                  className="text-[#eded1e]"
                                />
                                Telefon z odpowiedzią
                              </span>
                              <span className="text-[11px] text-zinc-400">
                                Oddzwonimy na podany numer
                              </span>
                            </div>
                          </button>

                          <button
                            type="button"
                            role="radio"
                            aria-checked={phoneResponsePreference === 'thread'}
                            onClick={() => setPhoneResponsePreference('thread')}
                            className={`flex items-center gap-2.5 p-3 rounded-xl border text-xs font-medium transition-all text-left cursor-pointer ${
                              phoneResponsePreference === 'thread'
                                ? 'bg-[#eded1e]/15 border-[#eded1e] text-white shadow-xs'
                                : 'bg-zinc-800/60 border-zinc-700/80 text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800'
                            }`}
                          >
                            <div
                              className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${
                                phoneResponsePreference === 'thread'
                                  ? 'border-[#eded1e] bg-[#eded1e]'
                                  : 'border-zinc-500'
                              }`}
                            >
                              {phoneResponsePreference === 'thread' && (
                                <div className="w-1.5 h-1.5 rounded-full bg-zinc-950" />
                              )}
                            </div>
                            <div className="flex flex-col min-w-0">
                              <span className="font-semibold text-white flex items-center gap-1.5">
                                <IconDeviceDesktop
                                  size={14}
                                  className="text-[#eded1e]"
                                />
                                Wątek w przeglądarce
                              </span>
                              <span className="text-[11px] text-zinc-400">
                                Odczytasz na tej stronie
                              </span>
                            </div>
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Row 2: Subject */}
              <div className="flex flex-col gap-1.5">
                <label
                  htmlFor="contact-subject"
                  className="text-xs sm:text-sm font-medium text-zinc-300 flex items-center gap-1.5"
                >
                  <IconFileText
                    size={15}
                    className="text-zinc-400"
                    aria-hidden="true"
                  />
                  Temat wiadomości <span className="text-rose-400">*</span>
                </label>
                <input
                  id="contact-subject"
                  name="subject"
                  type="text"
                  required
                  aria-label="Temat wiadomości"
                  placeholder="np. Pytanie o serwis amortyzatora lub dostępność terminu"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="w-full bg-zinc-800/90 hover:bg-zinc-800 focus:bg-zinc-850 text-white placeholder:text-zinc-500 border border-zinc-700/80 focus:border-[#eded1e] focus:ring-2 focus:ring-[#eded1e]/20 rounded-xl px-4 py-3 text-base outline-none transition-all"
                />
              </div>

              {/* Row 3: Message */}
              <div className="flex flex-col gap-1.5">
                <label
                  htmlFor="contact-message"
                  className="text-xs sm:text-sm font-medium text-zinc-300 flex items-center gap-1.5"
                >
                  <IconMessage
                    size={15}
                    className="text-zinc-400"
                    aria-hidden="true"
                  />
                  Treść wiadomości <span className="text-rose-400">*</span>
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  aria-label="Treść wiadomości"
                  rows={4}
                  placeholder="Opisz, w czym możemy Ci pomóc..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="w-full bg-zinc-800/90 hover:bg-zinc-800 focus:bg-zinc-850 text-white placeholder:text-zinc-500 border border-zinc-700/80 focus:border-[#eded1e] focus:ring-2 focus:ring-[#eded1e]/20 rounded-xl px-4 py-3 text-base outline-none transition-all resize-y min-h-[110px]"
                />
              </div>

              {/* Terms Checkbox */}
              <div className="pt-1">
                <label
                  htmlFor="contact-terms"
                  className="flex items-start gap-3 cursor-pointer select-none group"
                >
                  <input
                    id="contact-terms"
                    name="terms"
                    type="checkbox"
                    aria-label="Akceptacja regulaminu serwisu oraz zgoda na przetwarzanie danych"
                    checked={terms}
                    onChange={(e) => setTerms(e.target.checked)}
                    className="mt-1 w-4 h-4 rounded border-zinc-700 bg-zinc-800 text-[#eded1e] accent-[#eded1e] focus:ring-[#eded1e] focus:ring-offset-zinc-900 cursor-pointer"
                  />
                  <span className="text-xs sm:text-sm text-zinc-300 leading-snug group-hover:text-zinc-200 transition-colors">
                    Akceptuję{' '}
                    <Link
                      href={termsHref}
                      target="_blank"
                      color="warning"
                      underline="always"
                      font="sans"
                      weight="medium"
                    >
                      regulamin serwisu
                    </Link>{' '}
                    oraz wyrażam zgodę na przetwarzanie danych w celu udzielenia
                    odpowiedzi na zapytanie.
                  </span>
                </label>
              </div>

              <div className="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-zinc-800/60 border border-zinc-700/60 text-zinc-300 text-xs">
                <IconBell size={15} className="text-[#eded1e] shrink-0" />
                <span>
                  {contactMethod === 'email'
                    ? 'Otrzymasz automatyczne powiadomienie e-mail z bezpośrednim linkiem do podglądu odpowiedzi.'
                    : phoneResponsePreference === 'call'
                      ? 'Odpowiedź otrzymasz telefonicznie na wskazany numer.'
                      : 'Odpowiedź odczytasz w wątku na tej stronie w tej samej przeglądarce.'}
                </span>
              </div>

              {isProduction && (
                <div className="pt-1">
                  {appCheckStatus === 'verified' && (
                    <div className="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-emerald-950/40 border border-emerald-800/40 text-emerald-300 text-xs">
                      <IconShieldCheck
                        size={16}
                        className="text-emerald-400 shrink-0"
                      />
                      <span>
                        Bezpieczne połączenie zweryfikowane pomyślnie.
                      </span>
                    </div>
                  )}
                  {appCheckStatus === 'checking' && (
                    <div className="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-zinc-800/60 border border-zinc-700/60 text-zinc-400 text-xs">
                      <IconLoader2
                        size={16}
                        className="animate-spin text-zinc-400 shrink-0"
                      />
                      <span>Sprawdzanie zabezpieczeń...</span>
                    </div>
                  )}
                  {appCheckStatus === 'failed' && (
                    <div className="flex items-center justify-between gap-2 px-3.5 py-2 rounded-xl bg-rose-950/40 border border-rose-800/40 text-rose-300 text-xs">
                      <div className="flex items-center gap-2">
                        <IconAlertCircle
                          size={16}
                          className="text-rose-400 shrink-0"
                        />
                        <span>Nie udało się potwierdzić zabezpieczenia.</span>
                      </div>
                      <button
                        type="button"
                        onClick={verifyAppCheck}
                        className="inline-flex items-center gap-1 underline hover:text-white font-medium cursor-pointer"
                      >
                        <IconRefresh size={12} />
                        Ponów
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Error Banner */}
              {errorMsg && (
                <div className="p-3 rounded-xl bg-rose-900/40 border border-rose-700/50 text-rose-200 text-xs sm:text-sm flex items-center gap-2">
                  <IconAlertCircle
                    size={16}
                    className="text-rose-400 shrink-0"
                  />
                  <span>{errorMsg}</span>
                </div>
              )}

              {/* Submit Button */}
              <div className="pt-2 flex flex-col items-center">
                <button
                  type="submit"
                  disabled={!canSubmit}
                  className={`w-full sm:w-auto min-w-[240px] px-8 py-3.5 rounded-xl font-bold text-sm sm:text-base flex items-center justify-center gap-2.5 transition-all duration-200 shadow-md ${
                    canSubmit
                      ? 'bg-[#eded1e] hover:bg-[#f6f62a] active:scale-[0.98] text-zinc-950 shadow-[#eded1e]/20 hover:shadow-[#eded1e]/40 cursor-pointer'
                      : 'bg-zinc-800 text-zinc-500 border border-zinc-700/80 cursor-not-allowed opacity-70'
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <IconLoader2 size={18} className="animate-spin" />
                      <span>Wysyłanie wiadomości...</span>
                    </>
                  ) : (
                    <>
                      <IconSend size={18} />
                      <span>Wyślij wiadomość</span>
                    </>
                  )}
                </button>
                {!canSubmit && !isSubmitting && (
                  <span className="text-[11px] text-zinc-300 mt-2 text-center">
                    {isProduction && appCheckStatus !== 'verified'
                      ? 'Oczekiwanie na weryfikację zabezpieczenia...'
                      : !terms
                        ? 'Zaznacz akceptację regulaminu, aby odblokować wysyłanie.'
                        : 'Uzupełnij wszystkie wymagane pola formularza.'}
                  </span>
                )}
              </div>
            </form>
          )}
        </div>

        {/* Footer Bottom Bar */}
        <div className="w-full pt-4 border-t border-zinc-800 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-zinc-400">
          <span>{copyrightText}</span>
          <div className="flex items-center gap-4">
            <Link
              href={termsHref}
              underline="hover"
              font="sans"
              weight="normal"
            >
              <span className="text-zinc-400 hover:text-zinc-200 transition-colors">
                Regulamin
              </span>
            </Link>
            <span className="text-zinc-600">•</span>
            <button
              type="button"
              onClick={openCookieSettings}
              className="text-zinc-400 hover:text-[#eded1e] transition-colors cursor-pointer bg-transparent border-none p-0 text-xs underline underline-offset-2"
            >
              Ustawienia cookies
            </button>
          </div>
        </div>
      </div>

      <TicketTrackerModal
        ticketId={activeTrackingId}
        accessToken={activeTrackingToken}
        isOpen={isModalOpen}
        onClose={closeTicketModal}
      />

      <MyTicketsDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        tickets={savedTickets}
        onOpenTicket={openTicket}
        onRemoveTicket={removeTicket}
      />
    </footer>
  );
}
